import { Button, CheckboxSelect, HStack, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { FinanceEnumsFileSharingTypeEnum } from '@genial/services/dist/models';
import { RouteProp, useRoute } from '@react-navigation/native';
import React from 'react';
import { useRecoilValue } from 'recoil';

import { CustomerDetailStackParamList } from '../../../../../../../routes/types';
import { useCustomerActions } from '../../../../../../../store/customer/actions';
import { isExportingPdfForCustomers } from '../../../../../../../store/customer/atom';
import { useReportsActions } from '../../../../../../../store/customerDetail/reports/actions';
import {
  reportComplementaryTitleOptionsState,
  reportMarketOptionState,
  reportMonthSelectedOptionsState,
  reportPeriodDatesState,
  reportPeriodOptionState,
  reportPeriodYearOptionState,
  reportTypeOptionState,
  reportYearBrokerageOptionState,
  reportYearOptionState,
} from '../../../../../../../store/customerDetail/reports/atom';
import {
  currentReportYearOption,
  reportMarkets,
  reportsComplementaryTitles,
  reportsMonths,
  reportsPeriod,
  reportsPeriodYears,
  reportsType,
  reportsYears,
} from '../../../../../../../store/customerDetail/reports/constants';
import { reportPeriodIsDailySelector } from '../../../../../../../store/customerDetail/reports/selectors';
import { ExportPdfDropdown } from '../../../../shared/ExportPdfDropdown';
import { SelectPeriod } from '../../SelectPeriod/Index';
import { SelectType } from '../../SelectType';

import { HeaderResponsiveProps } from '.';

export const HeaderDesktop: React.FC<HeaderResponsiveProps> = ({
  incomeTaxBaseYearReports,
  incomeTaxTypeReports,
}) => {
  const { params } = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();

  const accountNumber = params?.accountNumber;
  const cpfCnpj = params?.cpfCnpj;

  const reportPeriodIsDaily = useRecoilValue(reportPeriodIsDailySelector);
  const isExportingPdf = useRecoilValue(isExportingPdfForCustomers);

  const reportTypeOption = useRecoilValue(reportTypeOptionState);
  const reportPeriodOption = useRecoilValue(reportPeriodOptionState);
  const reportPeriodDates = useRecoilValue(reportPeriodDatesState);
  const reportMarketOption = useRecoilValue(reportMarketOptionState);
  const reportYearOption = useRecoilValue(reportYearOptionState);
  const reportYearBrokerageYearOption = useRecoilValue(reportYearBrokerageOptionState);
  const reportComplementaryTitleOption = useRecoilValue(reportComplementaryTitleOptionsState);
  const reportPeriodYearOption = useRecoilValue(reportPeriodYearOptionState);
  const reportMonthSelectedOptions = useRecoilValue(reportMonthSelectedOptionsState);

  const { downloadIncomeStatementFile } = useCustomerActions();

  const {
    updateReportTypeOption,
    updateReportMarketOption,
    updateReportMonthSelectedOption,
    updateReportPeriodOption,
    updateReportYearOption,
    updateReportPeriodYearOption,
    updateReportPeriodDates,
    updateReportComplementaryTitleOption,
    handleDownloadIncomeTaxComplementaryReport,
    updateReportFilterParams,
    updateReportYearBrokerageOption,
  } = useReportsActions();

  const periodIsLoading = incomeTaxBaseYearReports.loading;
  const reportsYearsOptionItems = incomeTaxBaseYearReports?.data || [];
  const titlesIsLoading = incomeTaxTypeReports.loading;
  const reportsComplementaryTitlesOptionItems = incomeTaxTypeReports?.data || [];
  const buttonComplementaryReportDownloadIsDisabled =
    periodIsLoading ||
    titlesIsLoading ||
    reportPeriodYearOption === '' ||
    reportComplementaryTitleOption === '';

  const buttonAnnualIncomeReportDownloadIsDisabled =
    periodIsLoading || titlesIsLoading || reportYearOption === '';

  const reportComplementaryItem = reportsComplementaryTitlesOptionItems?.find(
    (item) => item.value === reportComplementaryTitleOption
  );

  const fileTitle = reportComplementaryItem?.label || 'File';

  const handleDownloadAnnualIncome = (sharingType?: FinanceEnumsFileSharingTypeEnum) => {
    downloadIncomeStatementFile(cpfCnpj, Number(reportYearOption), sharingType);
  };

  const handleDownloadComplementaryReport = (sharingType?: FinanceEnumsFileSharingTypeEnum) => {
    handleDownloadIncomeTaxComplementaryReport({
      accountNumber,
      fileTitle,
      reportType: reportComplementaryTitleOption,
      yearReference: reportPeriodYearOption,
      sharingType,
    });
  };

  const brokerageOptionYearItems = [
    {
      label: String(currentReportYearOption),
      value: String(currentReportYearOption),
    },
    ...(reportsYearsOptionItems || []),
  ];

  const handleSearchBrokerageNotes = () => {
    updateReportFilterParams({
      reportYearOption: reportYearBrokerageYearOption,
      reportMarketOption,
      reportMonthSelectedOptions,
    });
  };

  const reportYearOptionSelectedLabelIndex = reportsYearsOptionItems?.findIndex(
    (item) => item.value === (reportYearOption as unknown as string)
  );

  const reportPeriodYearOptionSelectedLabelIndex = reportsYearsOptionItems?.findIndex(
    (item) => item.value === (reportPeriodYearOption as unknown as string)
  );

  const reportComplementaryOptionSelectedLabelIndex =
    reportsComplementaryTitlesOptionItems?.findIndex(
      (item) => item.value === (reportComplementaryTitleOption as unknown as string)
    );

  const reportMarketOptionSelectedLabelIndex = reportMarkets.items.findIndex(
    (item) => item.value === (reportMarketOption as unknown as string)
  );

  const reportYearBrokerageOptionSelectedLabelIndex = brokerageOptionYearItems?.findIndex(
    (item) => item.value === (reportYearBrokerageYearOption as unknown as string)
  );

  const reportTypeOptionSelectedLabelIndex = reportsType.items?.findIndex(
    (item) => item.value === (reportTypeOption as unknown as string)
  );

  return (
    <VStack
      p={{ base: Spacing['inline.nano'], sm: Spacing['inline.xxs'] }}
      space={Spacing['stack.xxxs']}>
      <HStack alignItems="center" justifyContent="space-between" zIndex={5}>
        <SelectType
          isDisabled={isExportingPdf}
          title={reportsType.title.default}
          onValueChange={(value) => updateReportTypeOption(value)}
          options={reportsType.items}
          selectedValue={
            reportsType.items[reportTypeOptionSelectedLabelIndex]?.label || 'Selecione'
          }
        />
        {reportTypeOption === 'margin' && <Button isDisabled>Buscar</Button>}
        {reportTypeOption === 'brokerage-note' && (
          <SelectPeriod
            dateRangePickerIsDisabled={!reportPeriodIsDaily || isExportingPdf}
            isDisabled={isExportingPdf}
            startDate={reportPeriodDates.startDate}
            endDate={reportPeriodDates.endDate}
            title={reportsPeriod.title.default}
            onChange={(value) => updateReportPeriodOption(value)}
            options={reportsPeriod.items}
            selectedValue={reportPeriodOption}
            onSelectDates={(initialDate, endDate) => {
              const selectedDates = { endDate, startDate: initialDate };
              updateReportPeriodDates(selectedDates);
              updateReportFilterParams({ reportPeriodDates: selectedDates });
            }}
          />
        )}
        {reportTypeOption === 'annual-income-report' && (
          <HStack alignItems="center" space={Spacing['inline.xxxs']}>
            <SelectType
              isDisabled={isExportingPdf}
              isLoading={periodIsLoading}
              title={reportsYears.title.default}
              onValueChange={(value) => updateReportYearOption(value)}
              options={reportsYearsOptionItems}
              selectedValue={
                reportsYearsOptionItems[reportYearOptionSelectedLabelIndex]?.label || 'Selecione'
              }
            />
            <HStack alignItems="center">
              <ExportPdfDropdown
                isExporting={isExportingPdf}
                isDisabled={buttonAnnualIncomeReportDownloadIsDisabled}
                onDownload={handleDownloadAnnualIncome}
                onSendToClient={() => handleDownloadAnnualIncome('Email')}
              />
            </HStack>
          </HStack>
        )}
        {reportTypeOption === 'complementary' && (
          <HStack alignItems="center" space={Spacing['inline.xxxs']}>
            <SelectType
              isLoading={periodIsLoading}
              isDisabled={isExportingPdf}
              title={reportsPeriodYears.title.default}
              onValueChange={(value) => updateReportPeriodYearOption(value)}
              options={reportsYearsOptionItems}
              selectedValue={
                reportsYearsOptionItems[reportPeriodYearOptionSelectedLabelIndex]?.label ||
                'Selecione'
              }
            />
            <SelectType
              isLoading={titlesIsLoading}
              isDisabled={isExportingPdf}
              title={reportsComplementaryTitles.title.default}
              onValueChange={(value) => updateReportComplementaryTitleOption(value)}
              options={reportsComplementaryTitlesOptionItems}
              selectedValue={
                reportsComplementaryTitlesOptionItems[reportComplementaryOptionSelectedLabelIndex]
                  ?.label || 'Selecione'
              }
            />
            <HStack alignItems="center">
              <ExportPdfDropdown
                isExporting={isExportingPdf}
                isDisabled={buttonComplementaryReportDownloadIsDisabled}
                onDownload={handleDownloadComplementaryReport}
                onSendToClient={() => handleDownloadComplementaryReport('Email')}
              />
            </HStack>
          </HStack>
        )}
      </HStack>
      {reportTypeOption === 'brokerage-note' && !reportPeriodIsDaily && (
        <HStack alignItems="center" justifyContent="space-between">
          <SelectType
            isDisabled={reportPeriodIsDaily}
            title={reportMarkets.title.default}
            onValueChange={(value) => updateReportMarketOption(value)}
            options={reportMarkets.items}
            selectedValue={
              reportMarkets.items[reportMarketOptionSelectedLabelIndex]?.label || 'Selecione'
            }
          />
          <HStack alignItems={'center'} space={Spacing['inline.xxxs']}>
            <SelectType
              isDisabled={reportPeriodIsDaily}
              title={reportsYears.title.default}
              onValueChange={(value) => updateReportYearBrokerageOption(value)}
              options={brokerageOptionYearItems}
              selectedValue={
                brokerageOptionYearItems[reportYearBrokerageOptionSelectedLabelIndex]?.label ||
                'Selecione'
              }
            />
            <CheckboxSelect
              label="Mês"
              isDisabled={reportPeriodIsDaily}
              placeholder="Atual"
              options={reportsMonths.items}
              handleSelect={(value) => updateReportMonthSelectedOption(value)}
              selectedOptions={reportMonthSelectedOptions}
            />
            <Button isDisabled={reportPeriodIsDaily} onPress={handleSearchBrokerageNotes}>
              Buscar
            </Button>
          </HStack>
        </HStack>
      )}
    </VStack>
  );
};
